import type { ContextData, DataTableRow } from '@wirechunk/schemas/context-data/context-data.d.ts';
import { SiteVertical } from '@wirechunk/ui/api';

type InsuranceAgencySettings = {
  agentFullName?: string | null;
  missionStatement?: string | null;
} | null;

type LocationForGlobalProps = {
  position: number;
  phoneNumber?: string | null;
  email?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  addressCity?: string | null;
  addressState?: string | null;
  addressPostalCode?: string | null;
};

type SiteWithGlobalPropsSettings = {
  vertical?: SiteVertical | null;
  locations?: ReadonlyArray<LocationForGlobalProps> | null;
  insuranceAgencySettings?: InsuranceAgencySettings;
  socialLinks?: {
    facebookUrl?: string | null;
    linkedinUrl?: string | null;
    tiktokUrl?: string | null;
    instagramUrl?: string | null;
  } | null;
};

const definedStringProps = (
  entries: ReadonlyArray<readonly [string, string | null | undefined]>,
): ContextData => {
  const props: ContextData = {};
  for (const [key, value] of entries) {
    if (value !== null && value !== undefined) {
      props[key] = value;
    }
  }
  return props;
};

const insuranceAgencyGlobalProps = (site: SiteWithGlobalPropsSettings): ContextData => {
  if (site.vertical !== SiteVertical.InsuranceAgency || !site.insuranceAgencySettings) {
    return {};
  }

  return definedStringProps([
    ['agentFullName', site.insuranceAgencySettings.agentFullName],
    ['missionStatement', site.insuranceAgencySettings.missionStatement],
  ]);
};

// Location-derived props for every site location, ordered by ascending position. Each entry uses the
// DataTableRow shape required by ContextData; bindings read fields from `data` (for example `locations[0].data.phone`).
const locationsGlobalProps = (site: SiteWithGlobalPropsSettings): ContextData => {
  const sorted = site.locations?.slice().sort((a, b) => a.position - b.position);
  if (!sorted?.length) {
    return {};
  }
  return {
    locations: sorted.map(
      (location): DataTableRow => ({
        type: 'DataTableRow',
        id: String(location.position),
        data: definedStringProps([
          ['phone', location.phoneNumber],
          ['email', location.email],
          ['addressLine1', location.addressLine1],
          ['addressLine2', location.addressLine2],
          ['addressCity', location.addressCity],
          ['addressState', location.addressState],
          ['addressPostalCode', location.addressPostalCode],
        ]),
      }),
    ),
  };
};

const siteSocialLinksGlobalProps = (site: SiteWithGlobalPropsSettings): ContextData => {
  if (!site.socialLinks) {
    return {};
  }

  return definedStringProps([
    ['facebookUrl', site.socialLinks.facebookUrl],
    ['linkedinUrl', site.socialLinks.linkedinUrl],
    ['tiktokUrl', site.socialLinks.tiktokUrl],
    ['instagramUrl', site.socialLinks.instagramUrl],
  ]);
};

export const siteGlobalProps = (site: SiteWithGlobalPropsSettings): ContextData => {
  return {
    ...locationsGlobalProps(site),
    ...insuranceAgencyGlobalProps(site),
    ...siteSocialLinksGlobalProps(site),
  };
};
